/**
 * Who is signed in.
 *
 * On load the app asks the server to exchange the httpOnly refresh cookie for
 * an access token. That is what makes a reload keep you signed in without ever
 * putting a credential somewhere script can read.
 */

import { createContext, type ReactNode, use, useCallback, useEffect, useState } from "react";

import { api } from "@/lib/api";
import type { CurrentUser } from "@/lib/types";

interface AuthValue {
  user: CurrentUser | null;
  /** True only while the initial cookie exchange is in flight. */
  restoring: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-read who is signed in, after something changed which conferences this
   * account reaches or what they are called. */
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        if (await api.restoreSession()) {
          const me = await api.me();
          if (!cancelled) setUser(me);
        }
      } catch {
        // No usable session. The login screen is the correct outcome, not an
        // error to show — arriving signed out is normal.
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await api.login(email, password);
    setUser(await api.me());
  }, []);

  const reload = useCallback(async () => {
    setUser(await api.me());
  }, []);

  const signOut = useCallback(async () => {
    // Cleared first, so the UI never shows a signed-in state backed by a
    // session the server has already ended.
    setUser(null);
    await api.logout();
  }, []);

  return (
    <AuthContext value={{ user, restoring, signIn, signOut, reload }}>{children}</AuthContext>
  );
}

export function useAuth(): AuthValue {
  const value = use(AuthContext);
  if (value === null) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
